import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import CfLoader from "../../common/CfLoader/CfLoader";
import toast from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Calendar from "../../resources/icons/calendar.svg";
import VerificationNavbar from "../CombinedValidate/VerificationNavbar";
import ProgressStepper from "../CombinedValidate/ProgressStepper";
import InformationIcon from "../../resources/icons/Information-blue-icon.svg";
import { useTranslation } from "react-i18next";
import { useBrowserWarningNavigate } from "../../hooks/useBrowserWarningNavigate";
import { TwoPagePdfViewer } from "../../components/TwoPagePdfViewer/TwoPagePdfViewer";

const MAX_UPLOAD_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_DOCUMENT_MIME_TYPES = [
  "image/png",
  "application/pdf",
  "image/jpeg",
];
const ALLOWED_DOCUMENT_EXTENSIONS = [".png", ".pdf", ".jpeg", ".jpg"];
const DOCUMENT_MIME_TYPE_BY_EXTENSION = {
  ".png": "image/png",
  ".pdf": "application/pdf",
  ".jpeg": "image/jpeg",
  ".jpg": "image/jpeg",
};
const DOCUMENT_ACCEPT_ATTRIBUTE = [
  ...ALLOWED_DOCUMENT_EXTENSIONS,
  ...ALLOWED_DOCUMENT_MIME_TYPES,
].join(",");
const CANDIDATE_FIELD_MAX_LENGTH = 100;
const COMMENTS_MAX_LENGTH = 200;

const LegacyUserDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [activeSection, setActiveSection] = useState("legacy");
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Data from previous step
  const [certificateData, setCertificateData] = useState({});
  const [formFields, setFormFields] = useState({});
  const [uploadedFile, setUploadedFile] = useState(null);
  const [postComments, setPostComments] = useState("");
  const [modifiedFields, setModifiedFields] = useState({});
  const [isFileModified, setIsFileModified] = useState(false);

  const fileInputRef = useRef(null);

  const steps = [
    t("verify:select_certificate"),
    t("verify:details"),
    t("verify:payment"),
    t("verify:summary"),
  ];

  // Load data from location state
  useEffect(() => {
    const stateData = location.state;
    if (!stateData) {
      navigate("/verifier/legacy/certificate-details");
      return;
    }

    setCertificateData({
      postVerificationLogId: stateData.postVerificationLogId,
      postIssuerName: stateData.postIssuerName,
      postIssuerCode: stateData.postIssuerCode,
      postProgrammeName: stateData.postProgrammeName,
      postProgrammeDisplayName: stateData.postProgrammeDisplayName,
      postProgrammeCode: stateData.postProgrammeCode,
      postBatchName: stateData.postBatchName,
      postBatchCode: stateData.postBatchCode,
      postCredentialTemplate: stateData.postCredentialTemplate,
      postTemplateName: stateData.postTemplateName,
      postCertificateName: stateData.postCertificateName,
      isOthersSelected: stateData.isOthersSelected,
      customProgrammeName: stateData.customProgrammeName,
      customBatchName: stateData.customBatchName,
      documentType: stateData.documentType,
      modeOfVerification: stateData.modeOfVerification,
    });

    if (stateData.formFields) {
      setFormFields(stateData.formFields || {});
    }

    if (stateData.uploadedFile) {
      setUploadedFile(stateData.uploadedFile);
    }

    if (stateData.postComments) {
      setPostComments(stateData.postComments);
    }
  }, [location.state, navigate]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (section === "vc") {
      navigate("/verifier/validate");
    } else if (section === "legacy") {
      navigate("/verifier/validate", {
        state: { showLegacySection: "legacy" },
      });
    }
  };

  const stablePdfUrl = React.useMemo(() => {
    if (!uploadedFile?.url) return null;
    return uploadedFile.url;
  }, [uploadedFile?.url]);

  const handleChange = (e) => {
    setPostComments(e.target.value.slice(0, COMMENTS_MAX_LENGTH));
    setModifiedFields((prev) => ({ ...prev, comments: true }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      comments: undefined,
    }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const allowedExtension = ALLOWED_DOCUMENT_EXTENSIONS.find((extension) =>
      fileName.endsWith(extension),
    );
    const hasAllowedExtension = Boolean(allowedExtension);
    const hasAllowedMimeType =
      !file.type || ALLOWED_DOCUMENT_MIME_TYPES.includes(file.type);

    if (!hasAllowedMimeType || !hasAllowedExtension) {
      const message = t("verify:invalid_upload_document_format");
      setUploadedFile(null);
      setIsFileModified(false);
      setErrors((prevErrors) => ({
        ...prevErrors,
        uploadDocument: message,
      }));
      toast.error(message);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const fileType =
      file.type || DOCUMENT_MIME_TYPE_BY_EXTENSION[allowedExtension];

    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      const message = t("verify:invalid_upload_document_size");
      setUploadedFile(null);
      setIsFileModified(false);
      setErrors((prevErrors) => ({
        ...prevErrors,
        uploadDocument: message,
      }));
      toast.error(message);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedFile({
        url: reader.result,
        type: fileType,
        name: file.name,
      });
      setIsFileModified(true);
      setErrors((prevErrors) => ({
        ...prevErrors,
        uploadDocument: undefined,
      }));
    };
    reader.readAsDataURL(file);
  };

  const getApiErrorMessage = async (error) => {
    if (!error?.response) return null;

    const errorData = await error.response.json().catch(() => ({}));
    return errorData.message || errorData.error || null;
  };

  // Update the form field change handlers
  const handleFormFieldChange = (key, value, newValue) => {
    setFormFields((prevFields) => ({
      ...prevFields,
      [key]: { ...value, default: newValue },
    }));

    // Track that this field was modified
    setModifiedFields((prev) => ({ ...prev, [key]: true }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [key]: undefined,
    }));
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const CustomDateInput = React.forwardRef(({ value, onClick, id }, ref) => (
    <div className="relative">
      <input
        id={id}
        type="text"
        value={value}
        onClick={onClick}
        ref={ref}
        className="w-full bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 pr-10"
        readOnly
      />
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <img
          src={Calendar}
          className="h-5 w-5 text-gray-400"
          alt="calenderIcon"
        />
      </div>
    </div>
  ));

  const formatLabel = (key) => {
    return key
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  };

  const normalizeFieldIdentifier = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const getCandidateFieldType = (key, value) => {
    const identifier = normalizeFieldIdentifier(`${key} ${value.label || ""}`);
    const hasNameToken = identifier.includes("name");

    if (
      hasNameToken &&
      (identifier.includes("student") ||
        identifier.includes("candidate") ||
        identifier.includes("recipient"))
    ) {
      return "studentName";
    }

    if (
      identifier.includes("rollnumber") ||
      identifier.includes("rollno") ||
      (identifier.includes("roll") && identifier.includes("number"))
    ) {
      return "rollNumber";
    }

    return null;
  };

  const isValidCandidateFieldValue = (fieldType, value) => {
    if (!value) return true;
    if (fieldType === "studentName") {
      return /^[\p{L}\s.'-]+$/u.test(value);
    }
    if (fieldType === "rollNumber") {
      return /^[a-zA-Z0-9\s./_-]+$/.test(value);
    }
    return true;
  };

  const getCandidateFieldValidationError = (fieldType, value) => {
    if (!fieldType) return null;

    if (value.length > CANDIDATE_FIELD_MAX_LENGTH) {
      return t("verify:field_character_limit_error", {
        max: CANDIDATE_FIELD_MAX_LENGTH,
      });
    }

    if (!isValidCandidateFieldValue(fieldType, value)) {
      return fieldType === "studentName"
        ? t("verify:student_name_invalid_characters")
        : t("verify:roll_number_invalid_characters");
    }

    return null;
  };

  const getCommentsValidationError = () =>
    postComments.length > COMMENTS_MAX_LENGTH
      ? t("verify:field_character_limit_error", {
          max: COMMENTS_MAX_LENGTH,
        })
      : errors.comments;

  const renderCharacterCounter = (currentLength, maxLength) => {
    const isOverLimit = currentLength > maxLength;

    return (
      <div className="mt-1 text-right text-xs">
        <span className={isOverLimit ? "text-red-600" : "text-gray-500"}>
          {Math.max(maxLength - currentLength, 0)}{" "}
          {t("verify:characters_left")}
        </span>
      </div>
    );
  };

  const renderFormField = (key, value) => {
    const fieldInputId = `legacy-${key}-input`;
    const fieldType = getCandidateFieldType(key, value);
    const fieldValue = (value.default ?? "").toString();
    const fieldError =
      errors[key] || getCandidateFieldValidationError(fieldType, fieldValue);

    if (value.type === "date") {
      return (
        <div key={key} className="w-full">
          <label
            className={`block mb-2 text-sm font-medium text-gray-900 ${
              value.required ? "after:content-['*'] after:text-red-600" : ""
            }`}
            htmlFor={fieldInputId}
          >
            {value.label || formatLabel(key)}
          </label>
          <DatePicker
            selected={value.default ? new Date(value.default) : null}
            onChange={(date) => {
              handleFormFieldChange(key, value, date.toISOString());
            }}
            dateFormat="dd/MM/yyyy"
            customInput={<CustomDateInput id={fieldInputId} />}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            showTimeSelect={value.showTime}
          />
          {errors[key] && (
            <span className="text-red-600 text-sm">{errors[key]}</span>
          )}
        </div>
      );
    } else {
      return (
        <div key={key} className="w-full">
          <label
            className={`block mb-2 text-sm font-medium text-gray-900 ${
              value.required ? "after:content-['*'] after:text-red-600" : ""
            }`}
            htmlFor={fieldInputId}
          >
            {value.label || formatLabel(key)}
          </label>
          <input
            required={value.required}
            type="text"
            id={fieldInputId}
            value={fieldValue}
            onChange={(e) => {
              handleFormFieldChange(key, value, e.target.value);
            }}
            maxLength={fieldType ? CANDIDATE_FIELD_MAX_LENGTH : undefined}
            aria-invalid={Boolean(fieldError)}
            className={`w-full bg-gray-50 border ${
              fieldError ? "border-red-600" : "border-gray-300"
            } text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5`}
            placeholder={value.placeholder || ""}
          />
          {fieldError && (
            <span className="text-red-600 text-sm">{fieldError}</span>
          )}
          {fieldType &&
            renderCharacterCounter(
              fieldValue.length,
              CANDIDATE_FIELD_MAX_LENGTH,
            )}
        </div>
      );
    }
  };

  // Updated handleNextClick function in LegacyUserDetails.jsx

  const handleNextClick = async () => {
    const newErrors = {};
    Object.entries(formFields).forEach(([key, value]) => {
      const label = value.label || formatLabel(key);
      const isRequired = value.required !== false;
      const rawValue = (value.default ?? "").toString();
      const val = rawValue.trim();
      const fieldType = getCandidateFieldType(key, value);
      const candidateFieldError = getCandidateFieldValidationError(
        fieldType,
        rawValue,
      );

      if (isRequired) {
        if (value.type === "date") {
          if (!value.default) {
            newErrors[key] = `${label} ${t("verify:is_required")}`;
          }
        } else {
          if (!val) {
            newErrors[key] = `${label} ${t("verify:is_required")}`;
          }
        }
      }

      if (!newErrors[key] && candidateFieldError) {
        newErrors[key] = candidateFieldError;
      }
    });

    if (!uploadedFile) {
      newErrors.uploadDocument = `${t("verify:please_upload_document")}`;
    }

    const commentsError = getCommentsValidationError();
    if (commentsError) {
      newErrors.comments = commentsError;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    // Determine document type from state data
    const documentType =
      certificateData.documentType ||
      (certificateData.isOthersSelected ? "Data" : "Legacy");
    const modeOfVerification =
      certificateData.modeOfVerification ||
      (certificateData.isOthersSelected ? "Data" : "Legacy");

    const isBackNavigation = !!certificateData.postVerificationLogId;

    let payload = {
      verification_log_id: certificateData.postVerificationLogId,
      org_code: certificateData.postIssuerCode,
      programme_name: certificateData.postProgrammeName,
      batch_name: certificateData.postBatchName,
    };

    // Only include comments if they were modified
    if (modifiedFields.comments || !isBackNavigation) {
      payload.comments = postComments;
    }

    // Only include data if any form field was modified
    const hasModifiedFormFields = Object.keys(modifiedFields).some(
      (key) => key !== "comments" && key !== "uploadDocument",
    );

    if (hasModifiedFormFields) {
      payload.data = Object.keys(formFields).reduce((acc, key) => {
        acc[key] = formFields[key].default;
        return acc;
      }, {});
    }

    // Only include document if it was modified
    if (isFileModified || !isBackNavigation) {
      payload.document = uploadedFile.url;
    }

    // Add additional fields based on document type
    if (!isBackNavigation) {
      if (certificateData.isOthersSelected || documentType === "Data") {
        // For Data/custom programme batch
        payload.org_code = certificateData.postIssuerCode;
        payload.programme_name =
          certificateData.postProgrammeName ||
          certificateData.customProgrammeName;
        payload.batch_name =
          certificateData.postBatchName || certificateData.customBatchName;
      } else {
        // For Legacy
        payload.category_code = certificateData.postCredentialTemplate;
        payload.org_code = certificateData.postIssuerCode;
        payload.programme_code = certificateData.postProgrammeCode;
        payload.batch_code = certificateData.postBatchCode;
      }
    }

    try {
      let response;
      let endpoint;

      //! May use EntityConstants.POST_LEGACY_PAYMENT for future reference
      // Decide which API to call based on document type
      // if (certificateData.isOthersSelected || documentType === "Data") {
      endpoint = EntityConstants.POST_FORM_CUSTOM_PROGRAMME_BATCH;
      response = await POST(endpoint, payload);
      // } else {
      //   endpoint = EntityConstants.POST_LEGACY_PAYMENT;
      //   response = await POST(endpoint, payload);
      // }

      if (response.ok) {
        const data = await response.json();

        toast.success(t("verify:certificate_details_saved"));

        const nextStepData = {
          ...certificateData,
          formData: Object.keys(formFields).reduce((acc, key) => {
            acc[key] = formFields[key].default;
            return acc;
          }, {}),
          uploadedFile,
          postComments,
          apiResponse: data.data,
          currentStep: data.data.is_payment_required ? 2 : 3,
          documentType: documentType,
          modeOfVerification: modeOfVerification,
        };

        if (data.data.is_payment_required) {
          navigate(
            `/verifier/legacy/payment-details/${data.data.verification_log_id}`,
            { state: nextStepData },
          );
        } else {
          navigate("/verifier/legacy/summary", { state: nextStepData });
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error(
          "Failed to verify certificate:",
          response.statusText,
          errorData,
        );
        toast.error(errorData.message || t("verify:failed_save_details"));
      }
    } catch (error) {
      console.error("Error while calling the API:", error);
      const apiErrorMessage = await getApiErrorMessage(error);
      toast.error(apiErrorMessage || t("verify:failed_save_details"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    const hasApiData =
      certificateData.postVerificationLogId && location.state?.apiResponse;

    let stateData;

    if (hasApiData) {
      const apiData = location.state.apiResponse;
      const isOthersSelected =
        typeof certificateData.isOthersSelected === "boolean"
          ? certificateData.isOthersSelected
          : apiData.programme_code === "Others" ||
            apiData.batch_code === "Others";

      stateData = {
        postIssuerName: apiData.issuer_name || certificateData.postIssuerName,
        postIssuerCode: apiData.org_code || certificateData.postIssuerCode,
        postProgrammeName:
          apiData.programme_name || certificateData.postProgrammeName,
        postProgrammeDisplayName:
          apiData.programme_display_name ||
          certificateData.postProgrammeDisplayName,
        postProgrammeCode:
          apiData.programme_code ||
          certificateData.postProgrammeCode ||
          "Others",
        postBatchName: apiData.batch_name || certificateData.postBatchName,
        postBatchCode:
          apiData.batch_code || certificateData.postBatchCode || "Others",
        postCredentialTemplate:
          apiData.category_code || certificateData.postCredentialTemplate,
        postCertificateName:
          apiData.certificate_name || certificateData.postCertificateName,
        isOthersSelected,
        customProgrammeName: isOthersSelected
          ? apiData.programme_name || certificateData.customProgrammeName || ""
          : certificateData.customProgrammeName,
        customBatchName: isOthersSelected
          ? apiData.batch_name || certificateData.customBatchName || ""
          : certificateData.customBatchName,
        formFields,
        uploadedFile,
        postComments,
      };
    } else {
      stateData = {
        postIssuerName: certificateData.postIssuerName,
        postIssuerCode: certificateData.postIssuerCode,
        postProgrammeName: certificateData.postProgrammeName,
        postProgrammeDisplayName: certificateData.postProgrammeDisplayName,
        postProgrammeCode: certificateData.postProgrammeCode,
        postBatchName: certificateData.postBatchName,
        postBatchCode: certificateData.postBatchCode,
        postCredentialTemplate: certificateData.postCredentialTemplate,
        postCertificateName: certificateData.postCertificateName,
        isOthersSelected: certificateData.isOthersSelected,
        customProgrammeName: certificateData.customProgrammeName,
        customBatchName: certificateData.customBatchName,
        formFields,
        uploadedFile,
        postComments,
      };
    }

    navigate("/verifier/legacy/certificate-details", { state: stateData });
  };

  // Handle browser back/forward navigation
  useBrowserWarningNavigate(
    t("verify:unsaved_changes_warning"),
    Object.keys(formFields).length > 0 || !!uploadedFile,
  );

  const commentsError = getCommentsValidationError();

  if (isLoading) {
    return (
      <div className="w-full min-h-screen bg-cfSecondary pt-20 pb-10 flex items-center justify-center">
        <CfLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-[6rem] pb-[3rem] bg-white px-4">
      <VerificationNavbar
        activeSection={activeSection}
        handleSectionChange={handleSectionChange}
      />
      <ProgressStepper steps={steps} currentStep={currentStep} />

      <div className="space-y-4">
        <div className="bg-[#EAEBFF] rounded p-4 flex items-start gap-2">
          <img src={InformationIcon} className="mt-[0.2rem]" alt="" />
          <p className="text-gray-900">
            {t("verify:verification_intro_issued_by")}{" "}
            <span className="font-semibold">
              {certificateData.postIssuerName || t("verify:unknown_issuer")}
            </span>{" "}
            {t("verify:under_the")}{" "}
            <span className="font-semibold">
              {certificateData.postProgrammeDisplayName ||
                certificateData.postProgrammeName ||
                certificateData.customProgrammeName ||
                t("verify:unknown_programme")}
            </span>{" "}
            {t("verify:programme")}
            {(certificateData.postBatchName ||
              certificateData.customBatchName) && (
              <>
                {t("verify:for_the")}{" "}
                <span className="font-semibold">
                  {certificateData.postBatchName ||
                    certificateData.customBatchName}
                </span>{" "}
                {t("verify:batch")}
              </>
            )}
            {(certificateData.postCertificateName ||
              certificateData.postTemplateName) && (
              <>
                {t("verify:using_the")}{" "}
                <span className="font-semibold">
                  {certificateData.postCertificateName ||
                    certificateData.postTemplateName}
                </span>
                {""} {t("verify:template")}
              </>
            )}
          </p>
        </div>
        <div className="w-full flex flex-col lg:flex-row gap-4">
          <div
            className={`flex flex-col gap-4 border border-black p-4 rounded-lg shadow-md ${
              uploadedFile ? "w-full lg:w-1/2" : "w-full"
            }`}
          >
            <div className="text-black text-lg font-semibold">
              {t("verify:candidate_details")}
            </div>
            <div className="w-full flex flex-col gap-4">
              {Object.entries(formFields).map(([key, value]) =>
                renderFormField(key, value),
              )}

              <div className="w-full">
                <label
                  className={`block mb-2 text-sm font-medium text-gray-900 after:content-['*'] after:text-red-600`}
                  htmlFor="legacy-upload-document-input"
                >
                  {t("verify:upload_document")}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    id="legacy-upload-document-input"
                    accept={DOCUMENT_ACCEPT_ATTRIBUTE}
                    onChange={(e) => {
                      handleFileUpload(e);
                    }}
                    ref={fileInputRef}
                    className={`w-full bg-gray-50 border ${
                      errors.uploadDocument
                        ? "border-red-600"
                        : "border-gray-300"
                    } text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5`}
                    required
                  />
                  {uploadedFile && (
                    <button
                      id="legacy-remove-upload-button"
                      type="button"
                      onClick={handleRemoveFile}
                      className="p-1 text-red-600 hover:bg-red-100 rounded-lg transition-all duration-500 hover:text-red-700 focus:outline-none"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                {errors.uploadDocument && (
                  <span className="text-red-600 text-sm">
                    {errors.uploadDocument}
                  </span>
                )}
                <div className="mt-2 text-xs text-gray-600">
                  <p>{t("verify:upload_document_size_limit")}</p>
                  <p>{t("verify:upload_document_allowed_formats")}</p>
                </div>
              </div>

              <div className="w-full">
                <label
                  className="block mb-2 text-sm font-medium text-gray-900"
                  htmlFor="legacy-comments-textarea"
                >
                  {t("verify:comments_optional")}
                </label>
                <textarea
                  name="message"
                  value={postComments}
                  onChange={handleChange}
                  id="legacy-comments-textarea"
                  rows="4"
                  maxLength={COMMENTS_MAX_LENGTH}
                  aria-invalid={Boolean(commentsError)}
                  className={`block w-full rounded-md bg-white px-2.5 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 ${
                    commentsError ? "outline-red-600" : "outline-gray-300"
                  }`}
                ></textarea>
                {commentsError && (
                  <span className="text-red-600 text-sm">
                    {commentsError}
                  </span>
                )}
                {renderCharacterCounter(
                  postComments.length,
                  COMMENTS_MAX_LENGTH,
                )}
              </div>
            </div>

            <div className="flex justify-between">
              <button
                id="legacy-user-details-back-button"
                type="button"
                onClick={handleBackClick}
                className="py-2.5 px-[3.40rem] text-sm font-medium text-gray-700 bg-white hover:!bg-gray-50 focus:outline-none rounded-[8px] border border-gray-300 focus:z-10 focus:ring-3 focus:ring-gray-100"
              >
                {t("verify:back")}
              </button>
              <button
                id="legacy-user-details-next-button"
                type="button"
                onClick={handleNextClick}
                className="py-2.5 px-[3.40rem] text-sm font-medium text-white bg-cfButton hover:bg-cfPrimaryDark focus:outline-none rounded-[8px] border border-gray-200 focus:z-10 focus:ring-3 focus:ring-gray-100"
              >
                {t("verify:next")}
              </button>
            </div>
          </div>

          {uploadedFile && (
            <div className="w-full lg:w-1/2">
              <div className="w-full border border-gray-300 rounded-lg overflow-hidden">
                <div className="w-full h-[400px] bg-gray-100 flex items-center justify-center">
                  {uploadedFile.type === "application/pdf" ? (
                    <TwoPagePdfViewer
                      pdfUrl={stablePdfUrl}
                      showZoom={false}
                      heightClassName="h-full"
                    />
                  ) : (
                    <img
                      src={uploadedFile.url}
                      alt="Certificate Preview"
                      className="object-contain w-full h-full"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LegacyUserDetails;
